import React from 'react';
import { Modal, Tag } from 'antd';
import dayjs from 'dayjs';

type RedirectHop = { from: string; to: string; status?: number };

export type ScanDetail = {
  url: string;
  finalUrl?: string;
  startedAt: string | number | Date;
  durationMs?: number;
  status?: 'ok' | 'error' | string;
  gpc?: boolean;
  redirectChain?: RedirectHop[];
  analysis?: {
    headers?: { issues?: string[]; missing?: string[] };
    cookies?: { issues?: string[] };
  };
  diff?: {
    newThirdPartyHosts?: string[];
    blockedUntilConsent?: Array<{ host: string; category?: string }>;
    cookies?: {
      added?: Array<{ name: string; domain?: string; expires?: string }>;
      removed?: Array<{ name: string; domain?: string }>;
      changed?: Array<{ name: string; domain?: string; from?: string; to?: string }>;
    };
  };
};

export function ScanDetailsModal(props: {
  open: boolean;
  loading: boolean;
  detail: ScanDetail | null;
  onClose: () => void;
}) {
  const { open, loading, detail, onClose } = props;
  return (
    <Modal
      title="Scan details"
      open={open}
      onCancel={onClose}
      onOk={onClose}
      width={820}
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      {loading && <div>Loading…</div>}
      {!loading && detail && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div>
            <div>
              <strong>URL:</strong> <code>{detail.url}</code>
            </div>
            {detail.finalUrl && (
              <div>
                <strong>Final URL:</strong> <code>{detail.finalUrl}</code>
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span>
                <strong>Started:</strong> {dayjs(detail.startedAt).format('YYYY-MM-DD HH:mm:ss')}
              </span>
              {typeof detail.durationMs === 'number' && (
                <span>
                  <strong>Duration:</strong> {detail.durationMs} ms
                </span>
              )}
              <span>
                <strong>Status:</strong>{' '}
                {detail.status === 'ok' ? (
                  <Tag color="green">ok</Tag>
                ) : (
                  <Tag color="red">{detail.status || 'error'}</Tag>
                )}
              </span>
              <span>
                <strong>GPC:</strong> {detail.gpc ? <Tag color="purple">on</Tag> : 'off'}
              </span>
            </div>
          </div>

          {Array.isArray(detail.redirectChain) && detail.redirectChain.length > 0 && (
            <div>
              <strong>Redirect chain</strong>
              <ol>
                {(detail.redirectChain as RedirectHop[]).map((h, i) => (
                  <li key={i}>
                    <code>{h.from}</code> → <code>{h.to}</code>
                    {typeof h.status === 'number' && <span> — {h.status}</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {detail.analysis?.headers && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <strong>Header issues</strong>
                <ul>
                  {(detail.analysis.headers.issues || []).length === 0 && <li>None</li>}
                  {(detail.analysis.headers.issues || []).map((m: string, i: number) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>Missing headers</strong>
                <ul>
                  {(detail.analysis.headers.missing || []).length === 0 && <li>None</li>}
                  {(detail.analysis.headers.missing || []).map((m: string, i: number) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {detail.analysis?.cookies && (
            <div>
              <strong>Cookie issues</strong>
              <ul>
                {(detail.analysis.cookies.issues || []).length === 0 && <li>None</li>}
                {(detail.analysis.cookies.issues || []).map((m: string, i: number) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          {detail.diff?.newThirdPartyHosts && (
            <div>
              <strong>New third‑party hosts</strong>
              <ul>
                {detail.diff.newThirdPartyHosts.length === 0 && <li>None</li>}
                {detail.diff.newThirdPartyHosts.map((h: string) => (
                  <li key={h}>
                    <code>{h}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detail.diff?.blockedUntilConsent && (
            <div>
              <strong>Blocked until consent</strong>
              <ul>
                {detail.diff.blockedUntilConsent.length === 0 && <li>None</li>}
                {detail.diff.blockedUntilConsent.map((b, i) => (
                  <li key={`${b.host}-${i}`}>
                    <code>{b.host}</code>
                    {b.category && <span> — {b.category}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detail.diff?.cookies && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <strong>Cookies added</strong>
                <ul>
                  {(detail.diff.cookies.added || []).length === 0 && <li>None</li>}
                  {(detail.diff.cookies.added || []).map((c, i) => (
                    <li key={`add-${i}`}>
                      <code>{c.name}</code>
                      {c.domain && <span> @ {c.domain}</span>}
                      {c.expires && <span> (exp {c.expires})</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>Cookies removed</strong>
                <ul>
                  {(detail.diff.cookies.removed || []).length === 0 && <li>None</li>}
                  {(detail.diff.cookies.removed || []).map((c, i) => (
                    <li key={`rm-${i}`}>
                      <code>{c.name}</code>
                      {c.domain && <span> @ {c.domain}</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>Cookies changed</strong>
                <ul>
                  {(detail.diff.cookies.changed || []).length === 0 && <li>None</li>}
                  {(detail.diff.cookies.changed || []).map((c, i) => (
                    <li key={`chg-${i}`}>
                      <code>{c.name}</code>
                      {c.domain && <span> @ {c.domain}</span>}
                      {(c.from || c.to) && (
                        <span>
                          {' '}
                          — {c.from ?? '∅'} → {c.to ?? '∅'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
